import { coalesceRead } from "./inFlightReads";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import config from "../config.js";
import { store } from "../store";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import {
    getWebAuthToken,
    getWebAuthTokenMirror,
} from "../utils/authTokenStorage";

const defaultHeader = {
    Accept: "*/*",
    "Content-Type": "application/json",
};

function currentPageUrl(): string {
    if (typeof window === "undefined" || !window.location) return "";
    const { origin, pathname, search } = window.location;
    if (!origin || !pathname) return "";
    return `${origin}${pathname}${search || ""}`.slice(0, 2048);
}

// Enhanced HTTP client with timeout, retry, and connection management
abstract class HttpClient {
    protected JWTToken: string;
    protected baseURL: string;
    protected instance: AxiosInstance;
    private readonly maxRetries: number = 3;
    private readonly retryDelay: number = 1000; // 1 second base delay
    private readonly requestTimeout: number = 60000; // 1 minute default timeout

    constructor() {
        this.baseURL = this.getBaseURL();
        this.JWTToken = store?.getState()?.auth?.accessToken || "";
        this.instance = axios.create({
            headers: defaultHeader,
            timeout: this.requestTimeout,
            // Browser-compatible settings
            maxRedirects: 5,
            validateStatus: (status) => status < 500, // Don't throw on 4xx errors
            // Serialize arrays as repeated query params (key=value1&key=value2) for Fastify compatibility
            paramsSerializer: {
                indexes: null, // Use repeated keys instead of array notation
            },
        });

        this.initializeInterceptors();
    }

    protected async streamNdjson(path: string, body: unknown, onEvent: (event: any) => void, signal: AbortSignal): Promise<void> {
        let token = this.getToken();
        try { token = (await getWebAuthToken()) || token; } catch {}
        const response = await fetch(`${this.getBaseURL()}${path}`, { method: "POST", signal,
            headers: { "Content-Type": "application/json", Accept: "application/x-ndjson", Authorization: `Bearer ${token}`, "X-Theo-Client": "horizon_web" }, body: JSON.stringify(body) });
        if (!response.ok || !response.body) throw new Error("Unable to stream ticket analysis.");
        const reader = response.body.getReader(), decoder = new TextDecoder();
        let buffered = "", completed = false;
        try {
            for (;;) {
                const { value, done } = await reader.read();
                buffered += done ? decoder.decode() : decoder.decode(value, { stream: true });
                let newline;
                while ((newline = buffered.indexOf("\n")) >= 0) {
                    const line = buffered.slice(0, newline); buffered = buffered.slice(newline + 1);
                    if (line.trim()) onEvent(JSON.parse(line));
                }
                if (done) { if (buffered.trim()) onEvent(JSON.parse(buffered)); completed = true; break; }
            }
        } finally { if (!completed) await reader.cancel().catch(() => {}); reader.releaseLock(); }
    }

    private getToken = (): string => {
        return store?.getState()?.auth?.accessToken || getWebAuthTokenMirror() || "";
    };

    private getBaseURL = (): string => {
        return config.apiUrl || "";
    };

    private initializeInterceptors = (): void => {
        // Request interceptor with enhanced error handling
        this.instance.interceptors.request.use(
            async (config) => {
                if (store) {
                    this.baseURL = this.getBaseURL();
                    this.JWTToken = this.getToken();
                }
                let token = "";
                try {
                    token = (await getWebAuthToken()) || this.getToken();
                } catch {
                    token = this.getToken();
                }
                this.JWTToken = token;
                config.baseURL = this.baseURL;
                if (token) {
                    config.headers["Authorization"] = `Bearer ${token}`;
                }


                config.headers["X-Theo-Client"] = "horizon_web";
                const pageUrl = currentPageUrl();
                if (pageUrl) {
                    config.headers["X-Theo-Page-Url"] = pageUrl;
                }
                // Add request ID for tracking
                config.headers["X-Request-ID"] = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                
                return config;
            },
            (error) => {
                console.error("Request interceptor error:", error);
                return Promise.reject(error);
            }
        );

        // Response interceptor with retry logic
        this.instance.interceptors.response.use(
            (response: AxiosResponse) => response,
            async (error) => {
                const config = error.config;
                
                // Don't retry if already retried or if it's a client error (4xx)
                if (!config || config.__retryCount >= this.maxRetries || (error.response?.status >= 400 && error.response?.status < 500)) {
                    return Promise.reject(error);
                }

                // Increment retry count
                config.__retryCount = config.__retryCount || 0;
                config.__retryCount++;

                // Calculate delay with exponential backoff
                const delay = this.retryDelay * Math.pow(2, config.__retryCount - 1);
                
                console.warn(`Request failed, retrying (${config.__retryCount}/${this.maxRetries}) after ${delay}ms:`, error.message);

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, delay));

                // Retry the request
                return this.instance(config);
            }
        );
    };
    
    /** Axios validateStatus allows 4xx without throwing; reject so callers see errors. */
    private rejectIfErrorStatus(response: AxiosResponse): void {
        if (response.status < 400) return;
        const data = response.data as Record<string, unknown> | undefined;
        const detail =
            typeof data?.details === "string" ? data.details.trim() : "";
        const errField =
            typeof data?.error === "string" ? data.error.trim() : "";
        const messageField =
            typeof data?.message === "string" ? data.message.trim() : "";
        // Prefer `details` (e.g. Fastify { error, details }) so UIs show the real reason, not a generic label.
        const msg =
            detail ||
            errField ||
            messageField ||
            `Request failed with status ${response.status}`;
        const apiCode =
            typeof data?.code === "string" ? (data.code as string) : undefined;
        const synthetic: any = new Error(msg);
        synthetic.response = response;
        if (apiCode) {
            synthetic.code = apiCode;
        }
        throw synthetic;
    }

    private isInvalidTokenResponse(response: AxiosResponse): boolean {
        if (response.status !== 401) return false;
        const data = response.data as Record<string, unknown> | undefined;
        const message = [
            data?.error,
            data?.message,
            data?.details,
            data?.code,
        ]
            .filter((value): value is string => typeof value === "string")
            .join(" ")
            .toLowerCase();

        return (
            message.includes("invalid_token") ||
            message.includes("jwt expired") ||
            message.includes("token expired") ||
            message.includes("authentication required")
        );
    }

    private requestWithAuthRetry = async (
        request: () => Promise<AxiosResponse>,
        options?: { allowErrorResponse?: boolean }
    ): Promise<AxiosResponse> => {
        let response = await request();

        if (this.isInvalidTokenResponse(response)) {
            try {
                const token = await getWebAuthToken({ forceRefresh: true });
                if (token) {
                    this.JWTToken = token;
                    response = await request();
                }
            } catch {
                // Fall through to the normal error handling for the original response.
            }
        }

        if (!options?.allowErrorResponse) {
            this.rejectIfErrorStatus(response);
        }
        return response;
    };

    // Enhanced HTTP methods with better error handling and logging
    public get = async (url: string, config?: object, query?: object) => {
        try {
            const { timeout, ...headers } = config as any || {};
            const response = await this.requestWithAuthRetry(() => this.instance.get(url, {
                headers,
                params: query,
                ...(timeout !== undefined ? { timeout } : {}),
            }));
            return Promise.resolve(response.data);
        } catch (error: any) {
            console.error(`GET ${url} failed:`, error.message);
            return Promise.reject(this.enhanceError(error, 'GET', url));
        }
    };

    // Opt-in for read-only page APIs; isolate users, tokens, hosts, headers and filters.
    protected getCoalesced = (url: string, headers?: object, query?: object) => {
        const key = JSON.stringify([
            this.getBaseURL(), this.getToken(), store?.getState()?.auth?.user?.email,
            url, headers || {}, query || {},
        ]);
        return coalesceRead(key, () => this.get(url, headers, query));
    };

    // A cached page is usable immediately. Keep the revalidation HTTP request alive
    // until completion (Cloud Run may suspend detached work after a response).
    protected getRevalidating = async (url: string, query: object, onUpdate?: (value: any) => void) => {
        const scope = JSON.stringify([this.getBaseURL(), this.getToken(), store?.getState()?.auth?.user?.email]);
        const response = await this.getCoalesced(url, {}, query);
        if (response?._ticketCache?.refreshRequired) {
            setTimeout(() => {
                void this.getCoalesced(url, {}, { ...query, cacheRefresh: "true" })
                    .then(fresh => {
                        const currentScope = JSON.stringify([this.getBaseURL(), this.getToken(), store?.getState()?.auth?.user?.email]);
                        if (scope === currentScope) onUpdate?.(fresh);
                    })
                    .catch(() => { /* A transient provider error must not erase the displayed page. */ });
            }, 0);
        }
        return response;
    };

    public getBlob = async (url: string, query?: object): Promise<Blob> => {
        try {
            const response = await this.requestWithAuthRetry(
                () => this.instance.get(url, {
                    params: query,
                    responseType: "blob",
                }),
                { allowErrorResponse: true }
            );
            if (response.status >= 400) {
                let message = `Request failed with status ${response.status}`;
                if (response.data instanceof Blob) {
                    const text = await response.data.text();
                    try {
                        const body = JSON.parse(text) as Record<string, unknown>;
                        const detail = typeof body.details === "string" ? body.details.trim() : "";
                        const error = typeof body.error === "string" ? body.error.trim() : "";
                        const bodyMessage = typeof body.message === "string" ? body.message.trim() : "";
                        message = detail || error || bodyMessage || message;
                    } catch {
                        if (text.trim()) message = text.trim();
                    }
                }
                throw Object.assign(new Error(message), { response });
            }
            return Promise.resolve(response.data as Blob);
        } catch (error: any) {
            console.error(`GET blob ${url} failed:`, error.message);
            return Promise.reject(this.enhanceError(error, "GET", url));
        }
    };

    public post = async (url: string, config?: object, data?: object, query?: object) => {
        try {
            const { timeout, ...headers } = config as any || {};
            const axiosConfig: any = {
                headers: headers,
                params: query,
            };
            if (timeout !== undefined) {
                axiosConfig.timeout = timeout;
            }
            const response = await this.requestWithAuthRetry(() => this.instance.post(url, data, axiosConfig));
            return Promise.resolve(response.data);
        } catch (error: any) {
            console.error(`POST ${url} failed:`, error.message);
            return Promise.reject(this.enhanceError(error, 'POST', url));
        }
    };

    public put = async (url: string, config?: object, data?: object, query?: object) => {
        try {
            const response = await this.requestWithAuthRetry(() => this.instance.put(url, data, {
                headers: config,
                params: query,
            }));
            return Promise.resolve(response.data);
        } catch (error: any) {
            console.error(`PUT ${url} failed:`, error.message);
            return Promise.reject(this.enhanceError(error, 'PUT', url));
        }
    };

    public patch = async (url: string, config?: object, data?: object, query?: object) => {
        try {
            const response = await this.requestWithAuthRetry(() => this.instance.patch(url, data, {
                headers: config,
                params: query,
            }));
            return Promise.resolve(response.data);
        } catch (error: any) {
            console.error(`PATCH ${url} failed:`, error.message);
            return Promise.reject(this.enhanceError(error, 'PATCH', url));
        }
    };

    public del = async (url: string, config?: object, query?: object) => {
        try {
            const response = await this.requestWithAuthRetry(() => this.instance.delete(url, {
                headers: config,
                params: query,
            }));
            return Promise.resolve(response.data);
        } catch (error: any) {
            console.error(`DELETE ${url} failed:`, error.message);
            return Promise.reject(this.enhanceError(error, 'DELETE', url));
        }
    };

    // Enhanced error handling with connection status information
    private enhanceError(error: any, method: string, url: string): any {
        // 5xx: Axios rejects before rejectIfErrorStatus — promote server `details` / body to `message`.
        if (error?.response) {
            const fromBody = getApiErrorMessage(error, "");
            if (fromBody) {
                error.message = fromBody;
            }
        }
        const message =
            error?.message ||
            getApiErrorMessage(error, `${method} ${url} failed`);
        const enhancedError: any =
            error instanceof Error ? error : new Error(message);
        enhancedError.message = message;
        enhancedError.response = error?.response;
        enhancedError.method = method;
        enhancedError.url = url;
        enhancedError.timestamp = new Date().toISOString();
        enhancedError.isNetworkError = !error.response;
        enhancedError.isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        enhancedError.isConnectionError = error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND';

        // Add user-friendly error messages
        if (enhancedError.isNetworkError) {
            enhancedError.userMessage = "Network connection issue. Please check your internet connection.";
        } else if (enhancedError.isTimeout) {
            enhancedError.userMessage = "Request timed out. Please try again.";
        } else if (enhancedError.isConnectionError) {
            enhancedError.userMessage = "Cannot connect to server. Please try again later.";
        } else if (error.response?.status === 401) {
            enhancedError.userMessage = "Authentication failed. Please log in again.";
        } else if (error.response?.status >= 500) {
            enhancedError.userMessage =
                error.message || "Server error. Please try again later.";
        } else {
            enhancedError.userMessage = error.message || "An unexpected error occurred.";
        }

        return enhancedError;
    }
}

export default HttpClient;
